import { useCallback } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { TranslationAPI, URLService } from '@/services';
import type { TranslationResponse, TranslationResult } from '@/types';
import { handleError } from '../../utils/errorHandler';
import type { TranslateTabState, TranslateTabAction } from './translateTabReducer';
import { downloadFile } from '@/utils/downloadProxy';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseTranslateActionsParams {
  state: TranslateTabState;
  dispatch: React.Dispatch<TranslateTabAction>;
  llmModelRef: React.MutableRefObject<string>;
}

export interface UseTranslateActionsResult {
  handleTranslate: () => Promise<void>;
  handleCopy: (text: string) => void;
  handleDownload: () => Promise<void>;
  handleSwap: () => void;
  handleReset: () => void;
  requireAuth: ReturnType<typeof useRequireAuth>['requireAuth'];
  isLoginModalOpen: boolean;
  loginMessage: string | null;
  closeLoginModal: () => void;
}

export function useTranslateActions({
  state,
  dispatch,
  llmModelRef,
}: UseTranslateActionsParams): UseTranslateActionsResult {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } = useRequireAuth();

  const {
    sourceText,
    sourceLanguage,
    targetLanguages,
    translationResult,
    enableAiBoost,
    fileMode,
    llmProvider,
  } = state;

  // ---------------------------------------------------------------------------
  // handleTranslate
  // ---------------------------------------------------------------------------

  const handleTranslate = useCallback(async () => {
    if (!sourceText.trim()) {
      showToast(
        'error',
        t('translate.errors.noTextTitle', { defaultValue: 'No Text' }),
        t('translate.errors.noTextMessage', { defaultValue: 'Please enter text to translate' })
      );
      return;
    }

    if (targetLanguages.length === 0) {
      showToast(
        'error',
        t('translate.errors.noLanguagesTitle', { defaultValue: 'No Languages Selected' }),
        t('translate.errors.noLanguagesMessage', {
          defaultValue: 'Please select at least one target language',
        })
      );
      return;
    }

    requireAuth(async () => {
      try {
        dispatch({ type: 'SET_IS_TRANSLATING', payload: true });
        dispatch({ type: 'SET_TRANSLATION_RESULT', payload: null });

        console.log('[TranslateTab] Sending request with file_mode:', fileMode);

        const response = await TranslationAPI.translateText({
          text: sourceText,
          source_language: sourceLanguage || 'auto',
          target_languages: targetLanguages,
          enable_ai_boost: enableAiBoost,
          file_mode: fileMode,
          ...(enableAiBoost && {
            llm_provider: llmProvider,
            llm_model: llmModelRef.current,
          }),
        });

        if (response.success) {
          dispatch({ type: 'SET_TRANSLATION_RESULT', payload: response });
          showToast(
            'success',
            t('translate.success.title', { defaultValue: 'Translation Complete' }),
            t('translate.success.message', {
              defaultValue: 'Text translated to {count} language(s)',
              count: response.translations.length,
            })
          );

          if (fileMode && response.translations.length > 0) {
            triggerLocalDownload(
              response.translations.map(
                (tr: { language: string; translated_text: string }) =>
                  `=== ${tr.language.toUpperCase()} ===\n${tr.translated_text}\n`
              ),
              `translations_${Date.now()}.txt`
            );
            showToast(
              'success',
              t('translate.downloaded.title', { defaultValue: 'Downloaded!' }),
              t('translate.downloaded.message', { defaultValue: 'Translations saved to file' })
            );
          }
        } else {
          throw new Error(response.error || 'Translation failed');
        }
      } catch (error) {
        const errorMessage = handleError(error, 'TranslateTab', 'Unknown error occurred');
        showToast(
          'error',
          t('translate.errors.failedTitle', { defaultValue: 'Translation Failed' }),
          errorMessage
        );
      } finally {
        dispatch({ type: 'SET_IS_TRANSLATING', payload: false });
      }
    }, t('auth.loginRequired.translate'));
  }, [
    sourceText,
    sourceLanguage,
    targetLanguages,
    enableAiBoost,
    fileMode,
    llmProvider,
    llmModelRef,
    dispatch,
    showToast,
    t,
    requireAuth,
  ]);

  // ---------------------------------------------------------------------------
  // handleCopy
  // ---------------------------------------------------------------------------

  const handleCopy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast(
        'success',
        t('translate.copied.title', { defaultValue: 'Copied!' }),
        t('translate.copied.message', { defaultValue: 'Translation copied to clipboard' })
      );
    },
    [showToast, t]
  );

  // ---------------------------------------------------------------------------
  // handleDownload
  // ---------------------------------------------------------------------------

  const handleDownload = useCallback(async () => {
    if (!translationResult || !translationResult.translations.length) return;

    if (translationResult.file_mode && translationResult.download_url) {
      try {
        const apiUrl = URLService.getAPIBaseUrl();
        const downloadUrl = `${apiUrl}${translationResult.download_url}`;
        await downloadFile(
          downloadUrl,
          `${translationResult.project_folder || 'translations'}.zip`
        );
        showToast(
          'success',
          t('translate.downloaded.title', { defaultValue: 'Downloaded!' }),
          t('translate.downloaded.message', { defaultValue: 'Translations saved to file' })
        );
        return;
      } catch (error) {
        console.error('Download error:', error);
        showToast(
          'error',
          t('translate.errors.failedTitle', { defaultValue: 'Translation Failed' }),
          handleError(error, 'TranslateTab', 'Could not download the file. Please try again.')
        );
        return;
      }
    }

    triggerLocalDownload(
      translationResult.translations.map(
        (tr: TranslationResult) => `=== ${tr.language.toUpperCase()} ===\n${tr.translated_text}\n`
      ),
      `translations_${Date.now()}.txt`
    );

    showToast(
      'success',
      t('translate.downloaded.title', { defaultValue: 'Downloaded!' }),
      t('translate.downloaded.message', { defaultValue: 'Translations saved to file' })
    );
  }, [translationResult, showToast, t]);

  // ---------------------------------------------------------------------------
  // handleSwap
  // ---------------------------------------------------------------------------

  const handleSwap = useCallback(() => {
    if (targetLanguages.length === 1 && translationResult?.translations[0]) {
      const newSourceText = translationResult.translations[0].translated_text;
      const newSourceLang = targetLanguages[0];
      const newTargetLang =
        sourceLanguage === 'auto' && translationResult.detected_language
          ? translationResult.detected_language
          : sourceLanguage;

      dispatch({
        type: 'SWAP_LANGUAGES',
        payload: { newSourceText, newSourceLang, newTargetLang },
      });
    }
  }, [sourceLanguage, targetLanguages, translationResult, dispatch]);

  // ---------------------------------------------------------------------------
  // handleReset
  // ---------------------------------------------------------------------------

  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  return {
    handleTranslate,
    handleCopy,
    handleDownload,
    handleSwap,
    handleReset,
    requireAuth,
    isLoginModalOpen,
    loginMessage,
    closeLoginModal,
  };
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function triggerLocalDownload(lines: string[], filename: string): void {
  const content = lines.join('\n');
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

// Re-export TranslationResponse so callers don't need a separate import just for the type
export type { TranslationResponse };
